const cleanParams = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
);

const CJ_CONFIRMATION_ERROR_CODES = new Set([
  "CJ_PASSWORD_CONFIRMATION_REQUIRED", "CJ_PASSWORD_CONFIRMATION_INVALID",
  "CJ_PASSWORD_CONFIRMATION_EXPIRED", "CJ_PASSWORD_CONFIRMATION_USER_MISMATCH",
  "CJ_PASSWORD_CONFIRMATION_SCOPE_INVALID",
]);
const createCJDropshippingApi = ({ baseUrl, getToken, confirmPassword: confirmPasswordHandler, getConfirmationToken, onConfirmationInvalid }) => {
  const request = async (path, { method = "GET", body, params, confirmationToken, confirmationAction } = {}) => {
    const query = new URLSearchParams(cleanParams(params)).toString();
    const token = typeof getToken === "function" ? getToken() : getToken;
    const activeConfirmationToken = confirmationToken || (typeof getConfirmationToken === "function" ? getConfirmationToken() : "");
    const response = await fetch(`${String(baseUrl || "").replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activeConfirmationToken ? { "X-Elakay-CJ-Confirmation": activeConfirmationToken } : {}),
        ...(confirmationAction ? { "X-Dropshipping-Action": confirmationAction } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body || {}) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (CJ_CONFIRMATION_ERROR_CODES.has(data.code)) onConfirmationInvalid?.(data);
      const error = new Error(data.message || data.error || `HTTP ${response.status}`);
      error.status = response.status;
      error.code = data.code;
      error.errors = data.errors;
      error.warnings = data.warnings;
      error.data = data.data;
      // Whole JSON body (e.g. the order Checkout's RATE_CHANGED / loss review).
      error.body = data;
      throw error;
    }
    return data;
  };

  return {
    confirmPassword: (password) => confirmPasswordHandler
      ? confirmPasswordHandler(password)
      : request("/security/dropshipping/confirm-password", { method: "POST", body: { password } }),
    listPlatforms: () => request("/dropshipping/platforms"),
    getPlatform: (platformId) => request(`/dropshipping/platforms/${platformId}`),
    createPlatform: (payload) => request("/dropshipping/platforms", { method: "POST", body: payload }),
    updatePlatform: (platformId, payload) => request(`/dropshipping/platforms/${platformId}`, { method: "PATCH", body: payload }),
    getConnection: () => request("/dropshipping/cj/connection"),
    getSignupInfo: () => request("/dropshipping/cj/signup-info", { method: "POST" }),
    getAccountStatus: () => request("/dropshipping/cj/account-status"),
    connectCJ: (credentials = {}) => request("/dropshipping/cj/connect", {
      method: "POST",
      body: cleanParams(credentials),
    }),
    testConnection: (credentials = {}) => request("/dropshipping/cj/test-connection", {
      method: "POST",
      body: cleanParams(credentials),
    }),
    disconnectCJ: () => request("/dropshipping/cj/connection", { method: "DELETE" }),
    getCategories: (platformId) => request(`/dropshipping/platform/${platformId}/categories`),
    // Forme normalisée {id,name,parentId,parentName,children} (page Categories).
    getCategoriesNormalized: () => request("/dropshipping/cj/categories"),
    getSuppliers: (platformId) => request(`/dropshipping/platform/${platformId}/suppliers`),
    getSupplierProducts: (supplierId, params) => request(`/dropshipping/supplier/${supplierId}/products`, { params }),
    getProducts: (params) => request("/dropshipping/products", { params }),
    getProduct: (productId) => request(`/dropshipping/products/${productId}`),
    getDefaultLoading: () => request("/dropshipping/default-loading"),
    getDefaultProducts: () => request("/dropshipping/default-loading/products"),
    updateDefaultLoading: (payload) => request("/dropshipping/default-loading", { method: "PATCH", body: payload }),
    listPersonalSuppliers: (params) => request("/dropshipping/suppliers", { params }),
    searchPersonalSuppliers: (name) => request("/dropshipping/suppliers/search", { params: { name } }),
    createPersonalSupplier: (payload) => request("/dropshipping/suppliers", { method: "POST", body: payload }),
    updatePersonalSupplier: (id, payload) => request(`/dropshipping/suppliers/${id}`, { method: "PATCH", body: payload }),
    deletePersonalSupplier: (id) => request(`/dropshipping/suppliers/${id}`, { method: "DELETE" }),
    addPersonalSupplierProduct: (id, product) => request(`/dropshipping/suppliers/${id}/products`, { method: "POST", body: product }),
    removePersonalSupplierProduct: (id, productId) => request(`/dropshipping/suppliers/${id}/products/${productId}`, { method: "DELETE" }),
    listProductGroups: () => request("/dropshipping/product-groups"),
    createProductGroup: (payload) => request("/dropshipping/product-groups", { method: "POST", body: payload }),
    updateProductGroup: (id, payload) => request(`/dropshipping/product-groups/${id}`, { method: "PATCH", body: payload }),
    deleteProductGroup: (id) => request(`/dropshipping/product-groups/${id}`, { method: "DELETE" }),
    addProductGroupProduct: (id, product) => request(`/dropshipping/product-groups/${id}/products`, { method: "POST", body: product }),
    removeProductGroupProduct: (id, productId) => request(`/dropshipping/product-groups/${id}/products/${productId}`, { method: "DELETE" }),
    // ONE destination country per call ({ endCountryCode, quantity,
    // externalVariantIds | selectAllVariants, refresh }) -- the multi-country
    // comparison calls it once per country so each country loads and fails
    // on its own.
    getProductShippingQuote: (productId, payload) => request(`/dropshipping/products/${productId}/shipping-quote`, { method: "POST", body: payload }),
    // Complete CJ variant list of a product (global "Select all variants").
    getProductVariants: (productId, { refresh = false } = {}) => request(`/dropshipping/products/${productId}/variants`, { params: refresh ? { refresh: "true" } : {} }),
    importProduct: (productId) => request(`/dropshipping/products/${productId}/import-to-elakay`, { method: "POST" }),
    syncProduct: (productId) => request(`/dropshipping/products/${productId}/sync`, { method: "POST" }),
    getSyncAllPreview: () => request("/dropshipping/sync/preview"),
    syncAll: (options = {}, confirmation = {}) => request("/dropshipping/sync/all", { method: "POST", body: { options }, ...confirmation }),
    getShippingQuote: (payload) => request("/dropshipping/shipping/quote", { method: "POST", body: payload }),
    getOrders: (params) => request("/dropshipping/orders", { params }),
    createSupplierOrder: (orderId) => request(`/dropshipping/orders/${orderId}/create-supplier-order`, { method: "POST", body: {} }),
    // logId désambiguïse quand une commande Elakay a deux commandes CJ pour
    // le même vendeur (lot "default" + lot "override", voir
    // groupItemsByPaymentBucket côté serveur) -- optionnel, ignoré côté
    // serveur quand une seule commande CJ existe pour ce vendeur+commande.
    getTracking: (orderId, logId) => request(`/dropshipping/orders/${orderId}/tracking`, { params: cleanParams({ logId }) }),
    getApiErrors: () => request("/dropshipping/api-errors"),

    // Mode de paiement des commandes CJ (manuel/automatique) et solde CJ Wallet.
    getPaymentSettings: () => request("/dropshipping/cj/payment-settings"),
    updatePaymentMode: (paymentMode) => request("/dropshipping/cj/payment-settings/mode", { method: "PATCH", body: { paymentMode } }),
    getWallet: () => request("/dropshipping/cj/wallet"),
    getCJNotifications: (params) => request("/dropshipping/cj/notifications", { params }),
    retryOrderPayment: (orderId, logId) => request(`/dropshipping/orders/${orderId}/retry-payment`, { method: "POST", body: cleanParams({ logId }) }),
    markOrderForManualPayment: (orderId, logId) => request(`/dropshipping/orders/${orderId}/mark-for-manual-payment`, { method: "POST", body: cleanParams({ logId }) }),
    prepareCJPayment: (orderId, logId) => request(`/dropshipping/orders/${orderId}/prepare-cj-payment`, { method: "POST", body: cleanParams({ logId }) }),
    // Order-level Checkout of ONE supplier order: current CJ carriers for that
    // order only, save the order's carrier (the choice only -- the server
    // prices it), pay CJ with the saved carrier after a final server check.
    getOrderCheckout: (orderId, logId, { refresh = false } = {}) => request(`/dropshipping/orders/${orderId}/checkout`, { params: cleanParams({ logId, refresh: refresh ? "1" : "" }) }),
    saveOrderCarrier: (orderId, { logId, carrierId, logisticsName }) => request(`/dropshipping/orders/${orderId}/checkout/carrier`, { method: "PUT", body: cleanParams({ logId, carrierId, logisticsName }) }),
    payOrderCheckout: (orderId, { logId, confirmLoss = false }) => request(`/dropshipping/orders/${orderId}/checkout/pay`, { method: "POST", body: { ...cleanParams({ logId }), confirmLoss: confirmLoss === true } }),
    getOrderCJStatus: (orderId, logId) => request(`/dropshipping/orders/${orderId}/cj-status`, { params: cleanParams({ logId }) }),

    // Mode test/live de la plateforme (admin uniquement -- AnimationSettings.
    // dropshippingOut.platformModes, source de vérité déjà lue par le bandeau
    // d'environnement du dashboard vendeur). Hors namespace /dropshipping,
    // ces routes vivent à la racine de l'API (voir elakay_routes/Animationsettings.js).
    getPlatformEnvironmentSettings: () => request("/animation-settings/me"),
    updatePlatformEnvironmentMode: (userId, dropshippingOut) =>
      request(`/animation-settings/user/${userId}`, { method: "PUT", body: { dropshippingOut } }),
    getAdminOverview: () => request("/dropshipping/admin/overview"),
    getAdminConnections: (params) => request("/dropshipping/admin/connections", { params }),

    // Import pré-import (wizard manuel/automatique) et gestion de la
    // synchronisation d'un produit CJ déjà importé (voir editProduct).
    getCJProducts: (params) => request("/dropshipping/cj/products", { params }),
    getCJProductDetails: (externalProductId) => request(`/dropshipping/cj/products/${externalProductId}`),
    importCJProduct: (externalProductId, payload, confirmation = {}) => request(`/dropshipping/cj/products/${externalProductId}/import`, { method: "POST", body: payload, ...confirmation }),
    // `includeAllVariants` clears dropshippingData.excludedVariantExternalIds
    // server-side before refreshing -- the only way to bring back a variant
    // excluded once at import time (see cjProductSyncService.syncCJProduct).
    // `variantSelection` ({mode:"all"} | {mode:"selected",
    // selectedExternalVariantIds}) = the ONE global variant selection, the
    // same for every destination country (Sync / Resync wizard).
    syncCJProductByElakayId: (productId, confirmation = {}, { includeAllVariants = false, variantSelection } = {}) =>
      request(`/dropshipping/cj/products/${productId}/sync`, {
        method: "POST",
        // No body at all for a plain sync (only the confirmation headers).
        ...(includeAllVariants || variantSelection
          ? { body: { includeAllVariants, ...(variantSelection ? { variantSelection } : {}) } }
          : {}),
        ...confirmation,
      }),
    deleteCJProduct: (productId, options = {}, confirmation = {}) => request(`/dropshipping/cj/products/${productId}`, { method: "DELETE", body: { options }, ...confirmation }),
    compareCJProduct: (productId, refresh = false) => request(`/dropshipping/cj/products/${productId}/compare`, { params: refresh ? { refresh: "true" } : {} }),
    updateSyncLocks: (productId, locks) => request(`/dropshipping/cj/products/${productId}/sync-locks`, { method: "PATCH", body: { locks } }),
    updateAutoSync: (productId, autoSync, confirmation = {}) => request(`/dropshipping/cj/products/${productId}/auto-sync`, { method: "PATCH", body: { autoSync }, ...confirmation }),
    // Override par produit : paiement CJ automatique même en mode global manuel.
    updateAutoPayOverride: (productId, autoPayOverride, confirmation = {}) => request(`/dropshipping/cj/products/${productId}/auto-pay`, { method: "PATCH", body: { autoPayOverride }, ...confirmation }),
    // Étape "Shipping & Price" du CJSyncModal -- price/costPrice/salePrice/
    // shipping/dropshippingData.{shippingPrice,totalSupplierCost,paymentFees,
    // elakayFees,totalFees,calculatedProfit,profitMargin,pricingMode}.
    updateCJPricing: (productId, payload, confirmation = {}) => request(`/dropshipping/cj/products/${productId}/pricing`, { method: "PATCH", body: payload, ...confirmation }),

    // Route générique produit (hors namespace /dropshipping) -- seuls
    // categoriesID/sellActive/salePrice sont acceptés pour un produit
    // dropshipping (voir DROPSHIPPING_ONLY_ALLOWED_FIELDS, elakay_controllers/
    // product/product.js updateProduct). Utilisé par CJCategoryMappingStep
    // pour enregistrer le mapping de catégories du CJSyncModal.
    updateElakayProduct: (productId, patch, confirmation = {}) => request(`/products/update/${productId}`, { method: "PATCH", body: patch, ...confirmation }),

    // Suppliers CJ paginés (liste + détail depuis la DB, distincts de
    // getSuppliers ci-dessus). `params` accepte keyword/country/status/sort/
    // page/limit -- keyword ici EST la "recherche" (pas de route séparée).
    listSuppliers: (params) => request("/dropshipping/cj/suppliers", { params }),
    getSupplierDetail: (supplierId) => request(`/dropshipping/cj/suppliers/${supplierId}`),
    syncSupplier: (supplierId) => request(`/dropshipping/cj/suppliers/${supplierId}/sync`, { method: "POST" }),
    // Tab "JSON" du modal fournisseur (Family/Admin uniquement, appliqué côté serveur).
    getSupplierConsole: (supplierId) => request(`/dropshipping/cj/suppliers/${supplierId}/console`),

    // Mapping avancé CJ -> Elakay (registre + sync-with-mapping) et Console (Family uniquement).
    getSyncRegistry: () => request("/dropshipping/cj/sync-registry"),
    syncCJProductWithMapping: (productId, mapping, locks, options) =>
      request(`/dropshipping/cj/products/${productId}/sync-with-mapping`, { method: "POST", body: { mapping, locks, options } }),
    getCJProductConsole: (productId) => request(`/dropshipping/cj/products/${productId}/console`),
    // Adressé par DropshippingProduct._id (cache CJ, même id que getProduct
    // ci-dessus) -- contrairement à getCJProductConsole, disponible même
    // avant import (le tab Console du modal l'utilise désormais toujours).
    getProductConsole: (productId) => request(`/dropshipping/products/${productId}/console`),
  };
};

module.exports = { createCJDropshippingApi };
